import { useEffect, useMemo, useRef, useState } from 'react'
import { createRecord, updateRecord, type ApiError } from './api'
import type { Rec, Section as Sec } from './model'
import { toast } from './toast'

const s = (v: unknown) => (v == null ? '' : String(v))

export function EditModal({
  section,
  record,
  onClose,
  onSaved,
}: {
  section: Sec
  record: Rec | null
  onClose: () => void
  onSaved: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const formFields = useMemo(() => section.fields.filter((f) => f.input), [section])

  const initial = useMemo(() => {
    const o: Record<string, string> = {}
    for (const f of formFields) {
      o[f.key] = f.input === 'bool' ? (record?.[f.key] ? '1' : '0') : s(record?.[f.key])
    }
    return o
  }, [formFields, record])

  const [form, setForm] = useState<Record<string, string>>(initial)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const d = dialogRef.current
    if (d && !d.open) d.showModal()
  }, [])

  const set = (key: string, val: string) => setForm((f) => ({ ...f, [key]: val }))

  const missing = formFields
    .filter((f) => f.required && !form[f.key].trim())
    .map((f) => f.label)

  async function save() {
    if (missing.length) {
      setError(`${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} required.`)
      return
    }
    setSaving(true)
    setError('')
    try {
      if (record) {
        // send only what changed
        const patch: Record<string, string | number> = {}
        for (const f of formFields) {
          if (form[f.key] !== initial[f.key]) {
            patch[f.key] = f.input === 'number' ? Number(form[f.key] || 0) : form[f.key]
          }
        }
        if (Object.keys(patch).length === 0) {
          onClose()
          return
        }
        await updateRecord(section.key, s(record.id), patch)
        toast('Saved')
      } else {
        const body: Record<string, string | number> = {}
        for (const f of formFields) {
          const v = form[f.key]
          if (f.input === 'bool') body[f.key] = v === '1' ? 1 : 0
          else if (v.trim()) body[f.key] = f.input === 'number' ? Number(v) : v
        }
        await createRecord(section.key, body)
        toast('Added')
      }
      onSaved()
      onClose()
    } catch (e) {
      setError((e as ApiError).message ?? 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault()
        onClose()
      }}
      onClose={onClose}
    >
      <div className="form">
        <h3>{record ? `Edit ${s(record[section.titleField]) || 'record'}` : `Add to ${section.label}`}</h3>
        <p className="k">Changes save only when you press Save.</p>

        {formFields.map((f) => (
          <div className="fld" key={f.key}>
            <label htmlFor={`f-${f.key}`}>
              {f.label}
              {f.required && ' *'}
            </label>
            {f.input === 'textarea' ? (
              <textarea
                id={`f-${f.key}`}
                rows={3}
                value={form[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
              />
            ) : f.input === 'select' ? (
              <select
                id={`f-${f.key}`}
                value={form[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
              >
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : f.input === 'bool' ? (
              <select
                id={`f-${f.key}`}
                value={form[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
              >
                <option value="0">Off</option>
                <option value="1">On</option>
              </select>
            ) : (
              <input
                id={`f-${f.key}`}
                type={f.input === 'date' ? 'date' : f.input === 'number' ? 'number' : 'text'}
                value={form[f.key]}
                placeholder={f.input === 'date' ? 'YYYY-MM-DD' : ''}
                onChange={(e) => set(f.key, e.target.value)}
              />
            )}
          </div>
        ))}

        {error && <p className="req">{error}</p>}

        <div className="row">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn p" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </dialog>
  )
}
